import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import z from 'zod';

import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface ChangeGpanelRoleActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  gPanelRoles: string[];
}

export const ChangeGpanelRoleActionSchema = z.object({
  type: z.literal('Change_gPanel_Role'),
  roleName: z.string().min(1, 'Role is required.'),
});

export type ChangeGpanelRoleActionType = z.infer<
  typeof ChangeGpanelRoleActionSchema
>;

// Type guard to check if an action is ChangeGpanelRoleActionType
function isChangeGpanelRoleAction(
  action: PolicyAction
): action is ChangeGpanelRoleActionType {
  return action.type === 'Change_gPanel_Role';
}

export default function ChangeGpanelRoleAction({
  setValue,
  watch,
  gPanelRoles,
  control,
  errors,
}: ChangeGpanelRoleActionProps) {
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Change_gPanel_Role');
  const actionAtIndex = actionData[idx];
  if (!isChangeGpanelRoleAction(actionAtIndex)) {
    return null;
  }

  const currentAction: ChangeGpanelRoleActionType = actionAtIndex;
  const error: FieldErrors<ChangeGpanelRoleActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={2}>
        <Controller
          name={`actionData.${idx}.roleName`}
          control={control}
          render={() => (
            <TextField
              inputProps={{
                id: 'gPanel-role',
              }}
              InputLabelProps={{
                htmlFor: 'gPanel-role',
              }}
              select
              value={currentAction.roleName}
              onChange={(event) => {
                setValue(`actionData.${idx}.roleName`, event.target.value, {
                  shouldValidate: true,
                });
              }}
              label="gPanel Role"
              sx={{ minWidth: 250 }}
              autoComplete="off"
              error={!!error?.roleName}
              helperText={error?.roleName?.message || ' '}
            >
              {gPanelRoles?.length ? (
                gPanelRoles?.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value={''}>No Roles Found</MenuItem>
              )}
            </TextField>
          )}
        />
      </Stack>
    </Stack>
  );
}
